/**
 * Camera Manager Service
 * 
 * Handles camera streams and ensures proper cleanup when the camera is no longer needed.
 * This helps prevent camera resource leakage when components unmount incorrectly.
 */

class CameraManager {
  constructor() {
    this.activeStreams = new Map();
    this.lastStreamId = 0;
    
    // Add page unload handler to ensure all cameras are stopped when page closes
    window.addEventListener('beforeunload', () => {
      this.stopAllCameras(false); // Don't force stop on unload
    });

    // Add visibility change handler to stop cameras when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.hasActiveCameras()) {
        console.log('Page hidden - stopping active cameras');
        this.stopAllCameras(false); // Don't force stop on visibility change
      }
    });
  }

  /**
   * Start a camera stream with a specific device
   * @param {string} deviceId - Camera device ID to use
   * @param {Object} constraints - Additional MediaStream constraints
   * @returns {Promise<Object>} - Object containing the stream and an ID to reference it
   */
  async startCamera(deviceId, constraints = {}) {
    try {
      // Generate a unique stream ID
      const streamId = `camera-stream-${++this.lastStreamId}`;
      
      console.log(`Starting camera stream with ID: ${streamId}`);
      
      // Stop any existing streams first
      this.stopAllCameras(false);
      
      // Configure video constraints
      const videoConstraints = {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        ...constraints
      };

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints
      });
      
      // Store the stream for cleanup
      this.activeStreams.set(streamId, stream);
      
      return {
        stream,
        streamId
      };
    } catch (error) {
      console.error('Error starting camera:', error);
      throw error;
    }
  }

  /**
   * Get available camera devices
   * @returns {Promise<Array>} List of available video input devices
   */
  async getDevices() {
    try {
      // Request permission first to get labeled devices
      await navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          // Immediately stop the test stream
          stream.getTracks().forEach(track => track.stop());
        })
        .catch(() => {
          // Ignore errors, we just need the permission prompt
        });

      // Now get the devices with labels
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      return videoDevices;
    } catch (error) {
      console.error('Error getting camera devices:', error);
      return [];
    }
  }

  /**
   * Stop a camera stream by its ID
   * @param {string} streamId - ID of the stream to stop
   * @returns {boolean} - Whether the stream was found and stopped
   */
  stopCamera(streamId) {
    if (!streamId) return false;
    
    console.log(`Stopping camera stream with ID: ${streamId}`);
    
    const stream = this.activeStreams.get(streamId);
    if (stream) {
      stream.getTracks().forEach(track => {
        console.log(`Stopping track: ${track.kind} (${track.label}) - ${track.readyState}`);
        track.stop();
        track.enabled = false;
      });
      stream.getTracks().forEach(track => track.dispatchEvent(new Event('ended')));
      this.activeStreams.delete(streamId);
      return true;
    }
    return false;
  }

  /**
   * Stop all active camera streams
   * @param {boolean} forceStop - Whether to force stop all media tracks (default: false)
   */
  stopAllCameras(forceStop = false) {
    console.log(`Stopping all camera streams (${this.activeStreams.size} active)`);
    
    // First stop all known streams
    this.activeStreams.forEach((stream, id) => {
      stream.getTracks().forEach(track => {
        console.log(`Stopping tracked stream: ${track.kind} (${track.label})`);
        track.stop();
        track.enabled = false;
        track.dispatchEvent(new Event('ended'));
      });
    });
    this.activeStreams.clear();
    
    // Only force stop if explicitly requested
    if (forceStop) {
      this.forceStopAllCameraTracks();
    }
  }
  
  /**
   * Force stop any active camera tracks by querying all media devices
   * This is a more aggressive approach to ensure all cameras are truly stopped
   * Should only be used when we know there are stuck media tracks
   */
  forceStopAllCameraTracks() {
    try {
      // Only attempt to stop tracks if we know there are active media streams
      if (navigator.mediaDevices && 
          typeof navigator.mediaDevices.enumerateDevices === 'function') {
        navigator.mediaDevices.enumerateDevices()
          .then(devices => {
            const hasVideoDevice = devices.some(device => device.kind === 'videoinput');
            const hasAudioDevice = devices.some(device => device.kind === 'audioinput');
            
            if (hasVideoDevice) {
              navigator.mediaDevices.getUserMedia({ video: true })
                .then(stream => {
                  stream.getTracks().forEach(track => {
                    console.log(`Force stopping video track: ${track.kind}`);
                    track.stop();
                    track.enabled = false;
                  });
                })
                .catch(() => {
                  // Ignore errors - this is just a cleanup attempt
                });
            }
            
            if (hasAudioDevice) {
              navigator.mediaDevices.getUserMedia({ audio: true })
                .then(stream => {
                  stream.getTracks().forEach(track => {
                    console.log(`Force stopping audio track: ${track.kind}`);
                    track.stop();
                    track.enabled = false;
                  });
                })
                .catch(() => {
                  // Ignore errors
                });
            }
          })
          .catch(error => {
            console.log('Unable to enumerate devices:', error);
          });
      }
    } catch (error) {
      console.error('Error in forceStopAllCameraTracks:', error);
    }
  }

  /**
   * Check if any cameras are active
   * @returns {boolean} - Whether any cameras are active
   */
  hasActiveCameras() {
    return this.activeStreams.size > 0;
  }
}

// Export a singleton instance
export default new CameraManager();